import type { BlockState } from "./blockSpec.ts";
import type { VoxelWorld } from "./VoxelWorld.ts";

interface GridPosition {
  x: number;
  y: number;
  z: number;
}

import {
  getBlockDefinition,
  getConnectionType,
  getDefaultBlockState,
  getPlacementType
} from "./blockRegistry.ts";

const HORIZONTAL_DIRECTIONS = [
  { key: "north", x: 0, z: -1 },
  { key: "east", x: 1, z: 0 },
  { key: "south", x: 0, z: 1 },
  { key: "west", x: -1, z: 0 }
];

function getFacingOffset(facing: string | undefined): {
  x: number;
  z: number;
} {
  const direction = HORIZONTAL_DIRECTIONS.find(
    (entry) => entry.key === facing
  );
  return direction
    ? { x: direction.x, z: direction.z }
    : { x: 0, z: -1 };
}

function mergeState(blockId, state) {
  return {
    ...getDefaultBlockState(blockId),
    ...(state || {})
  };
}

function isConnectionTarget(world, x, y, z, connectionType) {
  const neighborData = world.getBlockData(x, y, z);
  if (!neighborData) {
    return false;
  }

  const neighborDefinition = getBlockDefinition(neighborData.blockId);
  const neighborConnectionType = getConnectionType(neighborData.blockId);

  if (connectionType === "pane") {
    return (
      neighborConnectionType === "pane" ||
      neighborData.blockId === "glass" ||
      neighborData.blockId === "iron_bars" ||
      (
        !neighborDefinition.transparent &&
        neighborData.blockId !== "water"
      )
    );
  }

  if (connectionType === "fence") {
    return (
      neighborConnectionType === "fence" ||
      neighborConnectionType === "wall" ||
      (
        !neighborDefinition.transparent &&
        !getPlacementType(neighborData.blockId) &&
        neighborData.blockId !== "water"
      )
    );
  }

  if (connectionType === "wall") {
    return (
      neighborConnectionType === "wall" ||
      neighborConnectionType === "fence" ||
      (
        !neighborDefinition.transparent &&
        neighborData.blockId !== "water"
      )
    );
  }

  return false;
}

function computeConnectionState(world, x, y, z, blockId) {
  const connectionType = getConnectionType(blockId);
  if (!connectionType) {
    return null;
  }

  const currentState = world.getBlockState(x, y, z);
  const state: BlockState = { ...currentState };
  const connected: Record<string, boolean> = {};

  HORIZONTAL_DIRECTIONS.forEach(function (direction) {
    connected[direction.key] = isConnectionTarget(
      world,
      x + direction.x,
      y,
      z + direction.z,
      connectionType
    );

    state[direction.key] =
      connectionType === "wall"
        ? (connected[direction.key] ? "low" : "none")
        : String(connected[direction.key]);
  });

  if (connectionType === "wall") {
    const straightNorthSouth =
      connected.north &&
      connected.south &&
      !connected.east &&
      !connected.west;
    const straightEastWest =
      connected.east &&
      connected.west &&
      !connected.north &&
      !connected.south;
    const blockAbove = world.hasBlock(x, y + 1, z);

    state.up = String(
      blockAbove || !(straightNorthSouth || straightEastWest)
    );
  }

  return state;
}

export function refreshConnectionsAround(world, positions) {
  const targets = new Map<string, GridPosition>();

  function addTarget(x, y, z) {
    targets.set(x + "," + y + "," + z, { x, y, z });
  }

  (positions || []).forEach(function (position) {
    addTarget(position.x, position.y, position.z);
    HORIZONTAL_DIRECTIONS.forEach(function (direction) {
      addTarget(
        position.x + direction.x,
        position.y,
        position.z + direction.z
      );
    });
  });

  targets.forEach(function (position) {
    const blockId = world.getBlock(
      position.x,
      position.y,
      position.z
    );
    if (!blockId || !getConnectionType(blockId)) {
      return;
    }

    const nextState = computeConnectionState(
      world,
      position.x,
      position.y,
      position.z,
      blockId
    );

    world.setBlock(
      position.x,
      position.y,
      position.z,
      blockId,
      nextState
    );
  });
}

export function mirrorBlockStateAcrossX(blockId, inputState) {
  const state = { ...(inputState || {}) };

  if (state.facing === "east") {
    state.facing = "west";
  } else if (state.facing === "west") {
    state.facing = "east";
  }

  if (state.hinge === "left") {
    state.hinge = "right";
  } else if (state.hinge === "right") {
    state.hinge = "left";
  }

  if (state.shape === "inner_left") {
    state.shape = "inner_right";
  } else if (state.shape === "inner_right") {
    state.shape = "inner_left";
  } else if (state.shape === "outer_left") {
    state.shape = "outer_right";
  } else if (state.shape === "outer_right") {
    state.shape = "outer_left";
  }

  const east = state.east;
  if (east !== undefined || state.west !== undefined) {
    state.east = state.west;
    state.west = east;
  }

  return mergeState(blockId, state);
}

export function placeBlockWithRules(
  world,
  position,
  blockId,
  inputState
) {
  const state = mergeState(blockId, inputState);
  const placementType = getPlacementType(blockId);
  const changedPositions: GridPosition[] = [];

  if (
    placementType === "door" ||
    placementType === "double_plant"
  ) {
    const lowerState = {
      ...state,
      half: "lower"
    };
    const upperState = {
      ...state,
      half: "upper"
    };

    if (
      world.setBlock(
        position.x,
        position.y,
        position.z,
        blockId,
        lowerState
      )
    ) {
      changedPositions.push({ ...position });
    }

    const upperPosition = {
      x: position.x,
      y: position.y + 1,
      z: position.z
    };

    if (
      world.setBlock(
        upperPosition.x,
        upperPosition.y,
        upperPosition.z,
        blockId,
        upperState
      )
    ) {
      changedPositions.push(upperPosition);
    }
  } else if (placementType === "bed") {
    const facing = state.facing || "north";
    const offset = getFacingOffset(facing);
    const footState = {
      ...state,
      facing,
      part: "foot",
      occupied: "false"
    };
    const headState = {
      ...state,
      facing,
      part: "head",
      occupied: "false"
    };
    const headPosition = {
      x: position.x + offset.x,
      y: position.y,
      z: position.z + offset.z
    };

    if (
      world.setBlock(
        position.x,
        position.y,
        position.z,
        blockId,
        footState
      )
    ) {
      changedPositions.push({ ...position });
    }

    if (
      world.setBlock(
        headPosition.x,
        headPosition.y,
        headPosition.z,
        blockId,
        headState
      )
    ) {
      changedPositions.push(headPosition);
    }
  } else {
    if (
      world.setBlock(
        position.x,
        position.y,
        position.z,
        blockId,
        state
      )
    ) {
      changedPositions.push({ ...position });
    }
  }

  refreshConnectionsAround(world, changedPositions);
  return changedPositions.length;
}

export function removeBlockWithRules(world, position) {
  const existing = world.getBlockData(
    position.x,
    position.y,
    position.z
  );
  if (!existing) {
    return 0;
  }

  const changedPositions = [{ ...position }];
  let changed = world.removeBlock(
    position.x,
    position.y,
    position.z
  ) ? 1 : 0;

  const placementType = getPlacementType(existing.blockId);

  if (
    placementType === "door" ||
    placementType === "double_plant"
  ) {
    const half = existing.state.half || "lower";
    const companionY =
      half === "upper" ? position.y - 1 : position.y + 1;
    const companion = world.getBlockData(
      position.x,
      companionY,
      position.z
    );

    if (
      companion &&
      companion.blockId === existing.blockId &&
      companion.state.half !== half
    ) {
      if (
        world.removeBlock(
          position.x,
          companionY,
          position.z
        )
      ) {
        changed += 1;
        changedPositions.push({
          x: position.x,
          y: companionY,
          z: position.z
        });
      }
    }
  } else if (placementType === "bed") {
    const part = existing.state.part || "foot";
    const offset = getFacingOffset(existing.state.facing);
    const multiplier = part === "head" ? -1 : 1;
    const companionPosition = {
      x: position.x + offset.x * multiplier,
      y: position.y,
      z: position.z + offset.z * multiplier
    };
    const companion = world.getBlockData(
      companionPosition.x,
      companionPosition.y,
      companionPosition.z
    );

    if (
      companion &&
      companion.blockId === existing.blockId &&
      companion.state.part !== part
    ) {
      if (
        world.removeBlock(
          companionPosition.x,
          companionPosition.y,
          companionPosition.z
        )
      ) {
        changed += 1;
        changedPositions.push(companionPosition);
      }
    }
  }

  refreshConnectionsAround(world, changedPositions);
  return changed;
}
